import React, { useEffect, useMemo, useRef, useState } from "react";
import { PAYING_TIERS, PLAN_DISPLAY, PLAN_STYLES, displayPlan } from "../../../constants/customers";

// Multi-select website dropdown with tier shortcuts. Selection changes are applied
// immediately but never trigger a refetch: the page fetches a superset once and
// filters locally, so toggling is instant.
export default function WebsiteFilter({ websites, selectedIds, payingIds, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const toggle = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    onChange(Array.from(next));
  };

  const options = useMemo(() => {
    let list = websites || [];
    if (search) {
      const t = search.toLowerCase();
      list = list.filter(
        (w) => (w.website_name || "").toLowerCase().includes(t) || (w.website_id || "").toLowerCase().includes(t)
      );
    }
    return [...list].sort((a, b) => {
      const aSel = selectedIds.has(a.website_id) ? 0 : 1;
      const bSel = selectedIds.has(b.website_id) ? 0 : 1;
      if (aSel !== bSel) return aSel - bSel;
      return (a.website_name || "").localeCompare(b.website_name || "");
    });
  }, [websites, search, selectedIds]);

  const total = (websites || []).length;
  const summary =
    selectedIds.size === 0
      ? "No websites selected"
      : total > 0 && selectedIds.size === total
      ? "All websites"
      : `${selectedIds.size} website${selectedIds.size !== 1 ? "s" : ""}`;

  return (
    <div className='relative' ref={ref}>
      <label className='block text-body-xs font-medium text-text-secondary mb-1.5'>
        Websites ({selectedIds.size} selected)
      </label>
      <button
        type='button'
        onClick={() => setOpen((v) => !v)}
        className='flex items-center gap-2 px-3 py-2 text-body-xs bg-white border border-border-default rounded-xl hover:bg-surface-neutral focus:outline-none focus:ring-2 focus:ring-accent-purple/20 min-w-[200px] justify-between text-text-secondary'>
        <span className='truncate'>{summary}</span>
        <svg className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} fill='none' viewBox='0 0 24 24' stroke='currentColor'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
        </svg>
      </button>

      {open && (
        <div className='absolute z-50 mt-2 w-96 bg-white border border-border-subtle rounded-2xl shadow-lg overflow-hidden animate-fade-in'>
          <div className='p-3 border-b border-border-subtle'>
            <input
              type='text'
              placeholder='Search websites...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className='w-full px-3 py-2 text-body-xs bg-surface-neutral rounded-lg border-none focus:outline-none focus:ring-2 focus:ring-accent-purple/20 placeholder:text-text-tertiary'
            />
          </div>
          <div className='p-2 border-b border-border-subtle flex gap-1.5 flex-wrap'>
            <button
              type='button'
              onClick={() => onChange([...payingIds])}
              className='px-2 py-1 text-caption font-semibold bg-accent-purple/10 text-accent-purple rounded-md hover:bg-accent-purple/20 transition-colors'>
              All Paying
            </button>
            {PAYING_TIERS.map((tier) => (
              <button
                type='button'
                key={tier}
                onClick={() => onChange((websites || []).filter((w) => w.subscription_plan === tier).map((w) => w.website_id))}
                className={`px-2 py-1 text-caption font-semibold rounded-md hover:opacity-80 transition-opacity ${PLAN_STYLES[tier]}`}>
                {PLAN_DISPLAY[tier]}
              </button>
            ))}
            <div className='w-full h-px bg-border-subtle my-1'></div>
            <button
              type='button'
              onClick={() => onChange((websites || []).map((w) => w.website_id))}
              className='px-2 py-1 text-caption font-semibold bg-accent-green/10 text-accent-green rounded-md hover:bg-accent-green/20 transition-colors'>
              Select All
            </button>
            <button
              type='button'
              onClick={() => onChange([])}
              className='px-2 py-1 text-caption font-semibold bg-surface-neutral-subtle text-text-secondary rounded-md hover:bg-surface-neutral transition-colors'>
              Clear All
            </button>
          </div>
          <div className='max-h-64 overflow-y-auto p-2 scrollbar-thin'>
            {options.length === 0 ? (
              <p className='text-text-tertiary text-body-xs text-center py-4'>No websites found</p>
            ) : (
              options.map((w) => {
                const isSelected = selectedIds.has(w.website_id);
                const plan = w.subscription_plan;
                return (
                  <label
                    key={w.website_id}
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-surface-neutral transition-colors ${
                      isSelected ? "border-l-2 border-accent-purple bg-accent-purple/[0.03]" : ""
                    }`}>
                    <input
                      type='checkbox'
                      checked={isSelected}
                      onChange={() => toggle(w.website_id)}
                      className='rounded border-border-default text-accent-purple focus:ring-accent-purple/30'
                    />
                    <div className='flex-1 min-w-0'>
                      <p className='text-body-xs font-medium text-text-primary truncate flex items-center gap-2'>
                        {w.website_name || w.website_id}
                        {plan && (
                          <span
                            className={`px-1.5 py-0.5 text-caption uppercase font-bold rounded-md ${
                              PLAN_STYLES[displayPlan(plan)] || "bg-surface-neutral-subtle text-text-secondary"
                            }`}>
                            {PLAN_DISPLAY[displayPlan(plan)] || displayPlan(plan)}
                          </span>
                        )}
                      </p>
                      <p className='text-caption text-text-tertiary truncate'>{w.website_id}</p>
                    </div>
                  </label>
                );
              })
            )}
          </div>
          <div className='p-2 border-t border-border-subtle'>
            <button
              type='button'
              onClick={() => setOpen(false)}
              className='w-full px-3 py-2 text-body-xs font-medium bg-surface-neutral text-text-secondary rounded-lg hover:bg-surface-neutral-subtle transition-colors'>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
